import { authnClient } from '@frontend/auth';
import { isWeaveTokenActive, isWeaveTokenBufferActive } from '@frontend/auth-helpers';
import { type AuthRequestAccessTokenRefreshResult, shell } from '@frontend/shell-utils';
import { retryWithBackoff } from './retry-with-backoff';

// basically a complex way to say `{ token?: string, jtiToken?: string }` but this way keeps our types in sync with the actual
// return type of `authnClient.refreshWeaveToken()`, so if that ever changes we get a type error here to remind us to update the return type of our strategies as well.
export type AccessTokenRefreshTokens = Pick<
  Awaited<ReturnType<typeof authnClient.refreshWeaveToken>>,
  'token' | 'jtiToken'
>;

export type RefreshAccessTokenDecision =
  | { result: 'no-op' }
  | { result: 'signout' }
  | { result: 'refreshed'; tokens: AccessTokenRefreshTokens };

export class AccessTokenRefreshStrategyError extends Error {
  constructor(public readonly kind: 'transient' | 'fatal', message: string, public readonly code?: string) {
    super(message);
    this.name = 'AccessTokenRefreshStrategyError';
  }
}

export const isTransientAccessTokenRefreshError = (error: unknown): error is AccessTokenRefreshStrategyError => {
  return error instanceof AccessTokenRefreshStrategyError && error.kind === 'transient';
};

// sends IPC request to main process to perform the token refresh and return the new tokens, or throw an error if the refresh failed (either transiently or fatally)
async function requestWeaveTokenRefresh(): Promise<AccessTokenRefreshTokens> {
  const result = (await shell.invoke?.('auth:request-access-token-refresh', undefined)) as
    | AuthRequestAccessTokenRefreshResult
    | undefined;

  if (!result) {
    throw new AccessTokenRefreshStrategyError('fatal', 'Shell did not return a refresh result');
  }

  if (!result.success) {
    const kind = result.status === 'transient-failure' ? 'transient' : 'fatal';
    throw new AccessTokenRefreshStrategyError(
      kind,
      `Shell access-token refresh failed (${result.status})`,
      result.errorCode
    );
  }

  if (!result.token) {
    throw new AccessTokenRefreshStrategyError('fatal', 'Shell returned success but no token');
  }

  return {
    token: result.token,
    jtiToken: result.jti,
  };
}

async function executeBrowserRefresh(): Promise<RefreshAccessTokenDecision> {
  if (isWeaveTokenActive()) {
    return { result: 'no-op' };
  }

  if (!isWeaveTokenBufferActive()) {
    if (!authnClient.isUserAuthenticated()) {
      return { result: 'signout' };
    }

    return { result: 'no-op' };
  }

  const refreshedTokens = await retryWithBackoff(() => authnClient.refreshWeaveToken(), { retries: 1 });
  return {
    result: 'refreshed',
    tokens: {
      token: refreshedTokens.token,
      jtiToken: refreshedTokens.jtiToken,
    },
  };
}

async function executeElectronRefresh(): Promise<RefreshAccessTokenDecision> {
  if (isWeaveTokenActive()) {
    return { result: 'no-op' };
  }

  const refreshedTokens = await requestWeaveTokenRefresh();
  return {
    result: 'refreshed',
    tokens: refreshedTokens,
  };
}

export function isDesktopBrowserAuthShell() {
  return (
    shell.isShell &&
    shell.featureAvailability?.has('desktop-browser-auth') &&
    shell.featureAvailability?.has('request-access-token-refresh-enabled')
  );
}

export function createRequestAccessTokenRefreshStrategy(): () => Promise<RefreshAccessTokenDecision> {
  // electron strategy is only available if we're in the shell and the desktop browser auth feature is enabled
  // this uses IPC to ask the main process to perform the refresh, since the main process is the source of truth for refresh tokens and can handle errors/retries more robustly.
  if (isDesktopBrowserAuthShell()) {
    console.log('RequestAccessTokenRefreshStrategy: using electron strategy'); // temporary log to verify we're getting the expected strategy in shell
    return () => executeElectronRefresh();
  }

  // browser strategy is the default
  return () => executeBrowserRefresh();
}
